import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { DEFAULT_LANGUAGE, Language, isLanguage, localeOf, readBrowserLanguage, resolveLanguage, writeBrowserLanguage } from './language';
import { Messages, Namespace, messagesOf } from './messages';
import { readSession } from '../services/sessionCookie';

interface LanguageState {
  language: Language;
  /** The locale to write dates in, e.g. `date.toLocaleDateString(locale)` */
  locale: string;
  /** Shows the backoffice in a language, and keeps it as this browser's choice. Saving it to the account is the caller's job */
  setLanguage: (language: Language) => void;
}

const LanguageContext = createContext<LanguageState | null>(null);

// The language shown right now, for code outside components (currentMessages)
let currentLanguage: Language = DEFAULT_LANGUAGE;

const browserLocales = (): readonly string[] =>
  navigator.languages?.length ? navigator.languages : [navigator.language];

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    // The language saved with the session, so a reload starts in the account's language
    const accountLanguage = readSession()?.user?.language;
    // The sign-in screen, after signing out, stays in the account's language
    if (isLanguage(accountLanguage)) writeBrowserLanguage(accountLanguage);
    currentLanguage = resolveLanguage(accountLanguage, readBrowserLanguage());
    return currentLanguage;
  });

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const setLanguage = useCallback((next: Language) => {
    currentLanguage = next;
    writeBrowserLanguage(next);
    setLanguageState(next);
  }, []);

  const state = useMemo<LanguageState>(
    () => ({ language, locale: localeOf(language, browserLocales()), setLanguage }),
    [language, setLanguage],
  );

  return <LanguageContext.Provider value={state}>{children}</LanguageContext.Provider>;
};

export function useLanguage(): LanguageState {
  const state = useContext(LanguageContext);
  if (!state) throw new Error('useLanguage needs a LanguageProvider above it');
  return state;
}

/** One area's messages in the language shown: `const t = useMessages('shows')`, then `t.title` or `t.count(3)` */
export function useMessages<N extends Namespace>(namespace: N): Messages<N> {
  return messagesOf(useLanguage().language, namespace);
}

/** For code outside components (services, helpers): one area's messages in the language shown right now */
export function currentMessages<N extends Namespace>(namespace: N): Messages<N> {
  return messagesOf(currentLanguage, namespace);
}

/** For code outside components: the locale to write dates in */
export const currentLocale = (): string => localeOf(currentLanguage, browserLocales());
